import { useId, useMemo, useState, type ReactNode } from "react";
import { DenseTable, StatusBadge, useDialogFocus, type Column } from "../../components/index.ts";
import type { RoleState } from "../../state/identity.tsx";
import { WriteButton, ReadOnlyNote, canWrite } from "./WriteControls.tsx";
import { useAlertRules, useCreateAlertRule, useUpdateAlertRule } from "./use-alerts.ts";
import {
  ALERT_SEVERITY_OPTIONS,
  RULE_KIND_OPTIONS,
  RULE_OP_OPTIONS,
  parseLabelMap,
  ruleKindLabel,
  severityLabel,
  severityLevel,
  type AlertRule,
  type AlertRuleInput,
  type AlertSeverity,
  type RuleDefinition,
  type RuleKind,
  type RuleOp,
} from "./vocab.ts";

/**
 * The Rules editor region (task 11.13; design 10.1 rule model, 11 Alerts row, 12.4). It lists rules
 * (`GET /alerts/rules`, viewer; the built-in pack included), creates (`POST`) and replaces (`PUT`)
 * them (operator). The form is labelled and keyboard-operable (design 12.4) and is built from the
 * contract's `RuleDefinition`: the universal fields (name, kind, severity, enabled) plus the members
 * the chosen `kind` typically needs. Which members a kind REQUIRES is checked server-side against
 * the catalog (FR-10.6); a rule naming an uncatalogued metric/event is rejected with 422, which the
 * form surfaces verbatim. The server is the authority — the form never invents a field the contract
 * lacks.
 *
 * Built-in rules can be tuned (threshold, duration, severity, enabled) but not deleted — the editor
 * marks them and still allows a change (PUT), matching the contract.
 */
export function RulesEditor(props: { role: RoleState }): ReactNode {
  const query = useAlertRules();
  const [editing, setEditing] = useState<AlertRule | "new" | null>(null);

  const columns: readonly Column<AlertRule>[] = [
    {
      key: "enabled",
      header: "Enabled",
      width: "sm",
      cell: (r) =>
        r.enabled ? (
          <StatusBadge level="neutral" label="Enabled" size="sm" />
        ) : (
          <StatusBadge level="unknown" label="Disabled" size="sm" />
        ),
    },
    {
      key: "name",
      header: "Rule",
      isRowHeader: true,
      cell: (r) => (
        <span className="alr-rule">
          <span className="alr-rule__name">{r.name}</span>
          {r.builtin && <span className="alr-tag">built-in</span>}
        </span>
      ),
    },
    { key: "kind", header: "Kind", cell: (r) => ruleKindLabel(r.kind) },
    {
      key: "severity",
      header: "Severity",
      width: "sm",
      cell: (r) => (
        <StatusBadge level={severityLevel(r.severity)} label={severityLabel(r.severity)} size="sm" />
      ),
    },
    {
      key: "actions",
      header: "Actions",
      cell: (r) => (
        <WriteButton role={props.role} onClick={() => setEditing(r)}>
          {r.builtin ? "Tune" : "Edit"}
        </WriteButton>
      ),
    },
  ];

  return (
    <section className="alr-region" aria-label="Alert rules">
      <div className="alr-region__head">
        <h2 className="alr-region__title">Rules</h2>
        <WriteButton role={props.role} onClick={() => setEditing("new")} variant="primary">
          New rule
        </WriteButton>
      </div>
      <ReadOnlyNote role={props.role} />

      {query.isError && !query.error.isWaking ? (
        <p className="alr-field__error" role="alert">
          Rules could not load: {query.error.message}
        </p>
      ) : (
        <DenseTable
          caption="Alert rules, built-in pack included."
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No rules yet. Create one to start evaluating alerts."
        />
      )}

      {editing !== null && (
        <RuleForm
          role={props.role}
          existing={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

/* ---- Create / replace form ------------------------------------------------------------------ */

function RuleForm(props: { role: RoleState; existing: AlertRule | null; onClose: () => void }): ReactNode {
  const titleId = useId();
  const create = useCreateAlertRule();
  const update = useUpdateAlertRule();
  const mutation = props.existing === null ? create : update;
  const def = props.existing?.definition;

  const [name, setName] = useState(props.existing?.name ?? "");
  const [kind, setKind] = useState<RuleKind>(def?.kind ?? "rate_threshold");
  const [severity, setSeverity] = useState<AlertSeverity>(props.existing?.severity ?? "warning");
  const [enabled, setEnabled] = useState(props.existing?.enabled ?? true);

  const [metric, setMetric] = useState(def?.metric ?? "");
  const [event, setEvent] = useState(def?.event ?? "");
  const [whereText, setWhereText] = useState(labelsToText(def?.where));
  const [windowS, setWindowS] = useState(numStr(def?.window_s));
  const [op, setOp] = useState<RuleOp>((def?.op as RuleOp) ?? ">");
  const [value, setValue] = useState(numStr(def?.value));
  const [forS, setForS] = useState(numStr(def?.for_s));
  const [absentForS, setAbsentForS] = useState(numStr(def?.absent_for_s));

  const nameError = name.trim().length === 0 ? "A name is required." : null;
  const canSubmit = canWrite(props.role) && nameError === null && !mutation.isPending;

  const definition = useMemo<RuleDefinition>(() => {
    const d: RuleDefinition = { kind, severity };
    if (metric.trim().length > 0) d.metric = metric.trim();
    // `event` is a free-text input; `RuleDefinition.event` is the catalog event-name enum. The
    // server validates the name against the live catalog on write, so narrow the trimmed string to
    // the field type here rather than mirror the whole enum in the editor.
    if (event.trim().length > 0) d.event = event.trim() as NonNullable<RuleDefinition["event"]>;
    const where = parseLabelMap(whereText);
    if (Object.keys(where).length > 0) d.where = where;
    const w = toNum(windowS);
    if (w !== null) d.window_s = w;
    if (kind === "rate_threshold" || kind === "baseline_deviation") {
      d.op = op;
      const v = toNum(value);
      if (v !== null) d.value = v;
    }
    const f = toNum(forS);
    if (f !== null) d.for_s = f;
    if (kind === "absence") {
      const a = toNum(absentForS);
      if (a !== null) d.absent_for_s = a;
    }
    return d;
  }, [kind, severity, metric, event, whereText, windowS, op, value, forS, absentForS]);

  const submit = (): void => {
    if (!canSubmit) return;
    const body: AlertRuleInput = { name: name.trim(), definition, enabled };
    const onDone = { onSuccess: () => props.onClose() };
    if (props.existing === null) create.mutate(body, onDone);
    else update.mutate({ id: props.existing.id, body }, onDone);
  };

  const showThreshold = kind === "rate_threshold" || kind === "baseline_deviation";
  const showMetric = kind !== "event_match";
  const showEvent = kind === "event_match";
  const showAbsence = kind === "absence";

  // Trap/restore focus and wire Escape-to-close for the modal (design 12.4).
  const dialogRef = useDialogFocus<HTMLDivElement>(props.onClose);

  return (
    <div className="alr-dialog-backdrop" role="presentation" onClick={props.onClose}>
      <div
        ref={dialogRef}
        className="alr-dialog alr-dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId} className="alr-dialog__title">
          {props.existing === null ? "New rule" : props.existing.builtin ? `Tune “${props.existing.name}”` : `Edit “${props.existing.name}”`}
        </h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className="alr-field">
            <span className="alr-field__label">Name (required)</span>
            <input
              className="alr-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={128}
              required
              aria-invalid={name.length > 0 && nameError !== null}
            />
            {name.length > 0 && nameError !== null && (
              <span className="alr-field__error" role="alert">
                {nameError}
              </span>
            )}
          </label>

          <div className="alr-field-row">
            <label className="alr-field">
              <span className="alr-field__label">Kind</span>
              <select
                className="alr-input"
                value={kind}
                onChange={(e) => setKind(e.target.value as RuleKind)}
                disabled={props.existing?.builtin === true}
                title={props.existing?.builtin === true ? "A built-in rule's kind cannot change; tune its parameters." : undefined}
              >
                {RULE_KIND_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {ruleKindLabel(k)}
                  </option>
                ))}
              </select>
            </label>
            <label className="alr-field">
              <span className="alr-field__label">Severity</span>
              <select
                className="alr-input"
                value={severity}
                onChange={(e) => setSeverity(e.target.value as AlertSeverity)}
              >
                {ALERT_SEVERITY_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {severityLabel(s)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {showMetric && (
            <label className="alr-field">
              <span className="alr-field__label">Metric (catalog name)</span>
              <input
                className="alr-input"
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                placeholder="e.g. gateway_requests_total"
              />
            </label>
          )}
          {showEvent && (
            <label className="alr-field">
              <span className="alr-field__label">Event (catalog name)</span>
              <input
                className="alr-input"
                value={event}
                onChange={(e) => setEvent(e.target.value)}
                placeholder="e.g. decision.denied"
              />
            </label>
          )}

          <label className="alr-field">
            <span className="alr-field__label">Where (one k=v per line, optional)</span>
            <textarea
              className="alr-input"
              value={whereText}
              onChange={(e) => setWhereText(e.target.value)}
              rows={2}
              placeholder={"service=gateway-mcp"}
            />
          </label>

          <div className="alr-field-row">
            <label className="alr-field">
              <span className="alr-field__label">Window (seconds)</span>
              <input className="alr-input" type="number" min={1} max={86400} value={windowS} onChange={(e) => setWindowS(e.target.value)} />
            </label>
            {showThreshold && (
              <>
                <label className="alr-field alr-field--narrow">
                  <span className="alr-field__label">Op</span>
                  <select className="alr-input" value={op} onChange={(e) => setOp(e.target.value as RuleOp)}>
                    {RULE_OP_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="alr-field">
                  <span className="alr-field__label">Value</span>
                  <input className="alr-input" type="number" value={value} onChange={(e) => setValue(e.target.value)} />
                </label>
              </>
            )}
          </div>

          <div className="alr-field-row">
            <label className="alr-field">
              <span className="alr-field__label">For (seconds, optional)</span>
              <input className="alr-input" type="number" min={0} max={86400} value={forS} onChange={(e) => setForS(e.target.value)} />
            </label>
            {showAbsence && (
              <label className="alr-field">
                <span className="alr-field__label">Absent for (seconds)</span>
                <input className="alr-input" type="number" min={1} max={86400} value={absentForS} onChange={(e) => setAbsentForS(e.target.value)} />
              </label>
            )}
          </div>

          <label className="alr-field alr-field--checkbox">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="alr-field__label">Enabled</span>
          </label>

          <p className="alr-faint alr-dialog__hint">
            The server validates kind-specific requirements against the catalog; a rule naming an
            uncatalogued metric or event is rejected and the reason is shown here.
          </p>

          {mutation.isError && (
            <p className="alr-field__error" role="alert">
              {mutation.error.kind === "forbidden"
                ? "The server did not permit this. Creating or changing a rule requires the operator role."
                : mutation.error.message}
            </p>
          )}

          <div className="alr-dialog__actions">
            <WriteButton role={props.role} type="submit" variant="primary" disabled={!canSubmit} onClick={submit}>
              {mutation.isPending ? "Saving…" : props.existing === null ? "Create rule" : "Save changes"}
            </WriteButton>
            <button type="button" className="alr-btn" data-variant="ghost" onClick={props.onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---- small helpers -------------------------------------------------------------------------- */

function numStr(n: number | undefined): string {
  return n === undefined ? "" : String(n);
}
function toNum(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}
function labelsToText(labels: Readonly<Record<string, string>> | undefined): string {
  if (labels === undefined) return "";
  return Object.entries(labels)
    .map(([k, v]) => `${k}=${v}`)
    .join("\n");
}
